import { useState } from 'react';
import { IconAlertTriangle, IconArrowsDiff, IconBox, IconCalendarEvent, IconCertificate, IconChevronLeft, IconCrane, IconFileCheck, IconListDetails, IconTruck, IconClockHour4, IconCopy, IconDownload, IconExternalLink, IconFileAlert, IconFileText, IconHistory, IconLock, IconPencil, IconPlus, IconUpload, IconX, IconBan } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { CALC, codesOf, counts, cusRows, deltaText, dk, floorText, groupCount, PRICE_GROUPS, priceText, resolveCus, resolveLines, routeRows, SEAT, ST, stdRows, surRows, UNIT, vnd, type PriceFilter, type ShownSt } from './priceLogic';
import type { Icon } from '@tabler/icons-react';
import type { PriceGrp } from './priceTypes';
import p from './price.module.css';
import s from './priceCompact.module.css';
import './price.i18n';
import './pricec.i18n';

// Mint-ServicePrice-iPad (1180): danh sách thẻ theo nhóm + chi tiết giá 2:1. Giá BÁN niêm yết; giá sàn chỉ vai Quản lý. Chỉ đọc; nút ghi báo toast.
// Chưa làm: Ask HANA, báo giá đang dùng, lịch sử thay đổi (chưa có dữ liệu mẫu), form sửa / thêm / so sánh.

type Tab = 'std' | 'cus' | 'sur' | 'trf';
const params = () => new URLSearchParams(typeof location === 'undefined' ? '' : location.search);
function readState(): ScreenState | null {
  const v = params().get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const tile = (color: string) => ({ background: `color-mix(in srgb, ${color} 18%, var(--card))` });
const FILTER: Record<Exclude<PriceFilter, 'all'>, string> = { wait: 'prc.f.wait', exp: 'prc.f.exp', below: 'prc.f.below' };

function StChip({ k }: { k: ShownSt }) {
  return <span className={p.st} style={{ color: ST[k][1], background: ST[k][2] }}>{ST[k][0]}</span>;
}
function Code({ code, grp }: { code: string; grp: PriceGrp }) {
  const tr = useT();
  const g = PRICE_GROUPS.find((x) => x.k === grp)!;
  return <span className={p.code} style={{ width: 48, ...tile(g.color) }} title={tr('prc.feeCode', { code })}>{code}</span>;
}

export function PriceCompact() {
  const tr = useT();
  const toast = useToast();
  const linesQ = useList('priceLine', { limit: 200 });
  const cusQ = useList('priceCus', { limit: 200 });
  const surQ = useList('priceSur', { limit: 200 });
  const rtQ = useList('priceRoute', { limit: 200 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('std');
  const [role, setRole] = useState<'sale' | 'mgr'>(params().get('role') === 'sale' ? 'sale' : 'mgr');
  const [qf, setQf] = useState<PriceFilter>('all');
  const [all, setAll] = useState(false);
  const [grp, setGrp] = useState<PriceGrp | 'all'>('all');
  const [code, setCode] = useState('all');
  const [sel, setSel] = useState<string | null>(null);
  const [selV, setSelV] = useState<number | null>(null);

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (linesQ.isLoading || cusQ.isLoading || surQ.isLoading || rtQ.isLoading) state = 'loading';
  else if (linesQ.isError || cusQ.isError || surQ.isError || rtQ.isError) state = [linesQ.error, cusQ.error, surQ.error, rtQ.error].some((e) => e instanceof DataError && e.code === 'noperm') ? 'noperm' : 'error';
  else if ((linesQ.data?.items.length ?? 0) === 0) state = 'empty';
  const noperm = state === 'noperm';
  const ready = state === 'ready';
  const mgr = role === 'mgr';

  const lines = resolveLines(linesQ.data?.items ?? []);
  const cus = resolveCus(cusQ.data?.items ?? [], lines);
  const sc = { grp, code };
  const n = counts(lines, cus);
  const noWrite = () => toast({ msg: tr('prc.noWrite') });
  const soonTo = (what: string) => () => toast({ msg: tr('prc.soon', { what }) });
  const link = (what: string) => (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault();
    e.stopPropagation();
    soonTo(what)();
  };

  const std = ready ? stdRows(lines, sc, qf === 'below' ? 'all' : qf, all) : [];
  const cusV = ready ? cusRows(cus, sc, qf) : [];
  const surV = ready ? surRows(surQ.data?.items ?? [], sc) : [];
  const trfV = ready ? routeRows(rtQ.data?.items ?? []) : [];
  const nRows = tab === 'std' ? std.length : tab === 'cus' ? cusV.length : tab === 'sur' ? surV.length : trfV.length;
  const byGroup = <T,>(rows: T[], g: (r: T) => PriceGrp) => PRICE_GROUPS.filter((x) => grp === 'all' || x.k === grp).map((x) => ({ ...x, rows: rows.filter((r) => g(r) === x.k) })).filter((x) => x.rows.length > 0);

  const pickTab = (k: string) => {
    setTab(k as Tab);
    setSel(null);
    setSelV(null);
    if (k === 'std' && qf === 'below') setQf('all');
  };
  const chip = (k: Exclude<PriceFilter, 'all'>, num: number) => (
    <button key={k} type="button" className={`${p.mchip} ${p[`m_${k}`]} ${qf === k ? p.mchipOn : ''}`} aria-pressed={qf === k} aria-label={`${num} ${tr(FILTER[k]).toLowerCase()}`} title={tr(FILTER[k])} onClick={() => { setQf(qf === k ? 'all' : k); if (k === 'below') pickTab('cus'); }}>
      {k === 'wait' ? <IconClockHour4 size={14} stroke={1.8} aria-hidden="true" /> : k === 'exp' ? <IconCalendarEvent size={14} stroke={1.8} aria-hidden="true" /> : <IconFileAlert size={14} stroke={1.8} aria-hidden="true" />}
      {num}
    </button>
  );

  const pickGrp = (k: PriceGrp | 'all') => { setGrp(k); setCode('all'); setSel(null); setSelV(null); };
  const svName = grp === 'all' ? tr('prc.allSvc') : PRICE_GROUPS.find((g) => g.k === grp)!.name;
  const GICO: Record<PriceGrp, Icon> = { hq: IconFileCheck, kd: IconCertificate, tr: IconTruck, cg: IconCrane, kh: IconBox };
  const tree: Array<{ k: PriceGrp | 'all'; name: string; color: string; Ico: Icon }> = [{ k: 'all', name: tr('prc.allSvc'), color: 'var(--tx2)', Ico: IconListDetails }, ...PRICE_GROUPS.map((g) => ({ ...g, Ico: GICO[g.k] }))];
  const treeEl = (
    <nav className={`${p.tree} ${s.tree}`} aria-label={tr('prc.service')}>
      {tree.map((g) => {
        const c = groupCount(lines, cus, g.k);
        const on = g.k === grp;
        return (
          <div key={g.k}>
            <button type="button" className={`${p.node} ${on ? p.nodeOn : ''}`} aria-current={on} onClick={() => pickGrp(g.k)}>
              <span className={p.nIc} style={g.k === 'all' ? { background: 'var(--sub)', color: g.color } : { ...tile(g.color), color: 'var(--tx2)' }}><g.Ico size={15} stroke={1.8} aria-hidden="true" /></span>
              <span className={p.ell}>{g.name}</span>
              {c.wait > 0 && <span className={p.wait} title={tr('prc.f.wait')} aria-label={tr('prc.waitN', { n: c.wait })}><IconClockHour4 size={13} stroke={1.8} aria-hidden="true" />{c.wait}</span>}
              <span className={p.lb}>{c.n}</span>
            </button>
            {on && g.k !== 'all' && codesOf(lines, g.k).map((cd) => (
              <button key={cd} type="button" className={`${p.kid} ${code === cd ? p.kidOn : ''}`} aria-current={code === cd} onClick={() => setCode(code === cd ? 'all' : cd)}>
                <b>{cd}</b>
                <span className={`${p.ell} ${p.lb}`}>{lines.find((x) => x.code === cd)?.name}</span>
                <span className={p.lb}>{lines.filter((x) => x.code === cd).length}</span>
              </button>
            ))}
          </div>
        );
      })}
    </nav>
  );

  const selLine = tab === 'std' && sel ? lines.find((l) => l.id === sel) : undefined;
  const ver = selLine ? selLine.vers.find((v) => v.i === selV) ?? selLine.cur : undefined;
  const detail = selLine && ver ? (
    <section className={s.detail} aria-label={tr('pcc.detail')}>
      <div className={s.dHead}>
        <button type="button" className={s.back} aria-label={tr('pcc.backList')} title={tr('pcc.backList')} onClick={() => { setSel(null); setSelV(null); }}><IconChevronLeft size={18} aria-hidden="true" /></button>
        <Code code={selLine.code} grp={selLine.grp} />
        <div className={s.dTitle}>
          <h2>{selLine.name}</h2>
          <div className={s.dMeta}>
            <span title={tr('pcc.unit')}><IconBox size={13} stroke={1.8} aria-hidden="true" />{UNIT[selLine.unit]}</span>
            <span title={tr('prc.c.calc')}>{CALC[selLine.calc][0]}</span>
          </div>
        </div>
      </div>
      <div className={s.dAct}>
        <Button variant="primary" icon={<IconPencil size={16} aria-hidden="true" />} onClick={noWrite}>{mgr ? tr('pcc.edit') : tr('pcc.propose')}</Button>
        <span className={s.dTools}>
          <HeaderTool label={tr('pcc.clone')} icon={IconCopy} onClick={noWrite} />
          <HeaderTool label={tr('pcc.stop')} icon={IconBan} onClick={noWrite} />
        </span>
      </div>
      <div className={s.dBody}>
        {ver.i !== selLine.cur.i && (
          <div className={s.old} role="status">
            <IconHistory size={14} stroke={1.8} aria-hidden="true" />
            <span>{tr('pcc.viewing', { v: ver.v, st: ST[ver.st][0] })}</span>
            <button type="button" onClick={() => setSelV(null)}>{tr('pcc.current')}</button>
          </div>
        )}
        <div className={s.kpis}>
          <div className={s.kpi}><div className={p.lb}>{tr('pcc.std')}</div><div>{priceText(selLine, ver)}{selLine.route || selLine.calc === 'act' ? '' : ' ₫'}</div><div className={p.lb}>/ {UNIT[selLine.unit]}</div></div>
          {mgr && <div className={s.kpi}><div className={p.lb}>{tr('prc.floorT')}</div><div>{floorText(selLine, ver)}{floorText(selLine, ver) === '—' ? '' : ' ₫'}</div></div>}
          <div className={s.kpi}><div className={p.lb}>VAT</div><div>{selLine.vat}%</div></div>
          <div className={s.kpi}><div className={p.lb}>{tr('prc.eff')}</div><div>{dk(ver.from)} → {dk(ver.to)}</div>{ver.st !== 'on' && <div className={p.lb}>{ST[ver.st][0]}</div>}</div>
        </div>
        <div className={s.info}>
          <div><span className={p.lb}>{tr('prc.service')}</span><span>{PRICE_GROUPS.find((g) => g.k === selLine.grp)!.name}</span></div>
          <div><span className={p.lb}>{tr('prc.c.calc')}</span><span>{CALC[selLine.calc][0]}</span></div>
          <div><span className={p.lb}>{tr('prc.c.incl')}</span><span>{selLine.incl || '—'}</span></div>
        </div>
        <section className={s.sec}>
          <h3>{tr('prc.versions')}</h3>
          <div className={s.list}>
            {selLine.vers.slice().reverse().map((v) => (
              <button key={v.i} type="button" className={`${s.ver} ${v.i === ver.i ? s.verOn : ''}`} aria-current={v.i === ver.i} aria-label={`Xem ${v.v}`} onClick={() => setSelV(v.i === selLine.cur.i ? null : v.i)}>
                <b>{v.v}</b>
                <span className={s.verMid}>
                  <b>{priceText(selLine, v)}</b>
                  {mgr && floorText(selLine, v) !== '—' && <span className={p.lb}>{tr('pcc.floorV', { v: floorText(selLine, v) })}</span>}
                  <span className={p.lb}>{dk(v.from)} → {dk(v.to)}</span>
                </span>
                <StChip k={v.st} />
              </button>
            ))}
          </div>
        </section>
        {cus.some((c) => c.line === selLine.id) && (
          <section className={s.sec}>
            <h3>{tr('prc.t.cus')} <span className={p.lb}>{cus.filter((c) => c.line === selLine.id).length}</span></h3>
            <div className={s.list}>
              {cus.filter((c) => c.line === selLine.id).map((c) => (
                <div key={c.id} className={s.cRow}>
                  <span className={p.who}>
                    <button type="button" className={p.cusBtn} onClick={soonTo(tr('prc.cusDetail'))}>{c.cus}</button>
                    <a className={p.ct} href="/ezy/contracts" title={tr('prc.openCt', { ct: c.ct })} aria-label={tr('prc.openCt', { ct: c.ct })} onClick={link(tr('prc.contract'))}><IconFileText size={13} stroke={1.8} aria-hidden="true" />{c.ct}</a>
                  </span>
                  <span className={s.cPrice}>
                    <b>{vnd(c.price)}</b>
                    <span className={p.lb}>{deltaText(c)}</span>
                    {c.under && <span className={p.flag}><IconAlertTriangle size={12} stroke={1.8} aria-hidden="true" />{mgr ? tr('prc.below') : c.st === 'wait' ? tr('prc.needApr') : tr('prc.apr')}</span>}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </section>
  ) : null;

  const rowsEl = (
    <section className={s.pane} aria-label={tr('prc.paneLabel', { name: svName })}>
      <div className={s.pbar}>
        <Segmented
          label={tr('prc.table')}
          value={tab}
          onChange={pickTab}
          items={[{ key: 'std', label: tr('prc.t.std') }, { key: 'cus', label: tr('prc.t.cus') }, { key: 'sur', label: tr('prc.t.sur') }, { key: 'trf', label: 'Trucking' }]}
        />
        <span className={`${p.lb} ${s.cnt}`} title={tr('prc.rows')}>{svName} · {nRows}</span>
        <span className={p.right}>
          {qf !== 'all' && (
            <button type="button" className={p.qfx} aria-label={tr('prc.unfilter', { f: tr(FILTER[qf]) })} title={tr('prc.unfilterT')} onClick={() => setQf('all')}>
              {tr(FILTER[qf])}
              <IconX size={12} stroke={2.4} aria-hidden="true" />
            </button>
          )}
          {tab === 'std' && <Segmented label={tr('prc.versions')} value={all ? 'all' : 'cur'} onChange={(k) => setAll(k === 'all')} items={[{ key: 'cur', label: tr('prc.eff') }, { key: 'all', label: tr('prc.allVers') }]} />}
          {tab === 'trf' && (
            <a className={p.open} href="/ezy/route-price" onClick={link(tr('prc.routePrice'))}>
              <IconExternalLink size={16} stroke={1.8} aria-hidden="true" />
              {tr('prc.openCalc')}
            </a>
          )}
        </span>
      </div>
      <div className={s.scroll}>
        {tab === 'std' && byGroup(std, (r) => r.n.grp).map((g) => (
          <div key={g.k}>
            <div className={p.gh}><h3>{g.name}</h3><span className={p.lb}>{g.rows.length}</span></div>
            {g.rows.map(({ n: ln, x, old }) => {
              const sc2 = surRows(surQ.data?.items ?? [], { grp: 'all', code: ln.code }).length;
              const nx = old ? null : ln.next;
              return (
                <button key={`${ln.id}-${x.v}`} type="button" className={`${s.row} ${sel === ln.id ? s.rowOn : ''}`} style={{ opacity: old ? 0.62 : 1 }} aria-current={sel === ln.id} aria-label={tr('prc.seePrice', { code: ln.code, name: ln.name, unit: UNIT[ln.unit] })} onClick={() => { setSel(ln.id); setSelV(old ? x.i : null); }}>
                  <span className={s.r1}><Code code={ln.code} grp={ln.grp} /><b className={s.nm} title={ln.name}>{ln.name}</b><b className={s.rb} title={tr('pcc.std')}>{priceText(ln, x)}</b></span>
                  <span className={s.r2}>
                    <span><IconBox size={12} stroke={1.8} aria-hidden="true" />{UNIT[ln.unit]}</span>
                    <span title={tr('prc.calcT', { v: CALC[ln.calc][0] })}>{CALC[ln.calc][1]}</span>
                    <span title="VAT">VAT {ln.vat}%</span>
                    {mgr && <span title={tr('prc.floorT')}>{tr('pcc.floorV', { v: floorText(ln, x) })}</span>}
                    {sc2 > 0 && <span className={p.sur} title={tr('prc.surN', { n: sc2 })}>{tr('pcc.surPlus', { n: sc2 })}</span>}
                  </span>
                  <span className={s.incl} title={ln.incl ? tr('prc.inclT', { v: ln.incl }) : tr('prc.noIncl')}>{ln.incl || '—'}</span>
                  <span className={s.r2}>
                    <StChip k={old ? x.st : ln.stx} />
                    <span>{x.v} · {dk(x.from)} → {dk(x.to)}</span>
                    {nx && (
                      <span className={s.nx} style={{ color: ST[nx.st][1] }} title={tr('prc.nextTip', { v: nx.v, st: ST[nx.st][0], from: dk(nx.from), p: priceText(ln, nx) })}>
                        <IconCalendarEvent size={14} stroke={1.8} aria-hidden="true" />
                        {tr('pcc.nextShort', { v: nx.v, from: dk(nx.from) })}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
        {tab === 'std' && std.length === 0 && <div className={p.empty}>{tr('prc.none')}</div>}

        {tab === 'cus' && byGroup(cusV, (r) => r.ln.grp).map((g) => (
          <div key={g.k}>
            <div className={p.gh}><h3>{g.name}</h3><span className={p.lb}>{g.rows.length}</span></div>
            {g.rows.map((c) => {
              const flag = c.under ? (mgr ? tr('prc.below') : c.st === 'wait' ? tr('prc.needApr') : tr('prc.apr')) : '';
              const flagT = c.under ? (mgr ? tr('prc.belowT', { floor: floorText(c.ln, c.ln.cur), who: c.st === 'wait' ? tr('prc.waitFor', { s: SEAT[c.seat] }) : tr('prc.aprBy', { s: SEAT[c.by || 'mgr'] }) }) : flag) : '';
              return (
                <div key={c.id} className={s.row} role="button" tabIndex={0} onClick={soonTo(tr('prc.cusDetail'))} onKeyDown={(e) => { if (e.key === 'Enter') soonTo(tr('prc.cusDetail'))(); }}>
                  <span className={s.r1}>
                    <Code code={c.ln.code} grp={c.ln.grp} />
                    <button type="button" className={`${p.cusBtn} ${s.nm}`} aria-label={tr('prc.seeCus', { cus: c.cus, code: c.ln.code })}>{c.cus}</button>
                    <b className={s.rb}>{vnd(c.price)}</b>
                  </span>
                  <span className={s.r2}>
                    <span>{c.ln.name} · {UNIT[c.ln.unit]}</span>
                    <span title={tr('pcc.std')}>{tr('pcc.stdShort')} {priceText(c.ln, c.ln.cur)}</span>
                    <span title={tr('prc.vsStd')}>{deltaText(c)}</span>
                    {mgr && <span>{tr('pcc.floorV', { v: floorText(c.ln, c.ln.cur) })}</span>}
                  </span>
                  <span className={s.r2}>
                    <a className={p.ct} href="/ezy/contracts" title={tr('prc.openCt', { ct: c.ct })} aria-label={tr('prc.openCt', { ct: c.ct })} onClick={link(tr('prc.contract'))}><IconFileText size={13} stroke={1.8} aria-hidden="true" />{c.ct}</a>
                    <StChip k={c.stx} />
                    {flag && <span role="img" className={p.flag} title={flagT} aria-label={flagT}><IconAlertTriangle size={12} stroke={1.8} aria-hidden="true" />{flag}</span>}
                    <span>{dk(c.from)} → {dk(c.to)}</span>
                  </span>
                </div>
              );
            })}
          </div>
        ))}
        {tab === 'cus' && cusV.length === 0 && <div className={p.empty}>{tr('prc.noCus')}</div>}

        {tab === 'sur' && surV.map((r) => (
          <div key={r.id} className={`${s.row} ${s.plain}`}>
            <span className={s.r1}><Code code={r.code} grp={r.grp} /><b className={s.nm}>{r.name}</b><b className={s.rb}>{r.price ? vnd(r.price) : '—'}</b></span>
            <span className={s.r2}><span>{r.formula.replace('{p}', r.price ? vnd(r.price) : '')}</span><StChip k={r.st} /></span>
          </div>
        ))}
        {tab === 'sur' && surV.length === 0 && <div className={p.empty}>{tr('prc.noSur')}</div>}

        {tab === 'trf' && trfV.map((r) => {
          const lab = tr('prc.openRoute', { from: r.from, to: r.to });
          return (
            <a key={r.id} className={s.row} href="/ezy/route-price" aria-label={lab} onClick={link(tr('prc.routePrice'))}>
              <span className={s.r1}><b className={s.nm}>{r.from}</b><span className={p.lb} aria-label={tr('prc.toWord')}>→</span><span className={s.nm}>{r.to}</span><span className={p.lb}>{r.km} km</span><IconExternalLink size={15} stroke={1.8} aria-hidden="true" /></span>
              <span className={s.r2}>
                <span>TRF 20 <b>{vnd(r.a20)}</b></span>
                <span>TRF 40 <b>{vnd(r.a40)}</b></span>
                <span>TRFA 40 <b>{vnd(r.aA)}</b></span>
                {mgr && <span>{tr('pcc.floor40', { v: vnd(r.flo) })}</span>}
                <StChip k="on" />
              </span>
            </a>
          );
        })}
        {tab === 'trf' && trfV.length === 0 && <div className={p.empty}>{tr('prc.noRoute')}</div>}
      </div>
    </section>
  );

  return (
    <Page kind="table">
      <PageHeader
        title={tr('prc.title')}
        meta={ready ? <>{chip('wait', n.wait)}{chip('exp', n.exp)}{chip('below', n.below)}</> : undefined}
        actions={
          <>
            <Segmented label={tr('prc.roleView')} value={role} onChange={(k) => setRole(k as 'sale' | 'mgr')} items={[{ key: 'sale', label: 'Sale' }, { key: 'mgr', label: tr('prc.mgr') }]} />
            <HeaderTool label={tr('prc.imp')} icon={IconUpload} onClick={noWrite} />
            <HeaderTool label={tr('prc.exp')} icon={IconDownload} onClick={soonTo(tr('prc.exp'))} />
            <HeaderTool label={tr('prc.history')} icon={IconHistory} onClick={soonTo(tr('prc.history'))} />
            <HeaderTool label={tr('prc.compare')} icon={IconArrowsDiff} onClick={soonTo(tr('prc.compare'))} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} disabled={noperm} onClick={noWrite}>{tr('prc.add')}</Button>
          </>
        }
      />
      <StateView
        state={noperm ? 'ready' : state}
        title={state === 'empty' ? tr('prc.empty') : undefined}
        desc={state === 'empty' ? tr('prc.emptyDesc') : undefined}
        icon={state === 'empty' ? <IconFileText size={26} stroke={1.8} /> : undefined}
        action={state === 'error' ? <Button onClick={() => void Promise.all([linesQ.refetch(), cusQ.refetch(), surQ.refetch(), rtQ.refetch()])}>{tr('prc.retry')}</Button> : undefined}
        skeleton={<Skeleton rows={9} />}
      >
        {noperm ? (
          <section className={p.none} role="status">
            <IconLock size={28} stroke={1.8} aria-hidden="true" />
            <b>{tr('prc.noperm')}</b>
          </section>
        ) : (
          <div className={s.body}>
            {rowsEl}
            {detail ?? treeEl}
          </div>
        )}
      </StateView>
    </Page>
  );
}
